/**
 * Permissions API — real integration
 * Routes:
 *   GET  /api/admin/menus/permissions/:roleId  → fetch role permissions
 *   POST /api/admin/menus/permissions/:roleId  → save role permissions
 *   GET  /api/role                             → fetch all roles
 *   GET  /api/admin/menus                      → fetch menu tree
 */

import axios from "axios";
import { authHeaders } from "./nodeAuthService";
import { API_BASE_URL } from "../config/api";

const MENUS_URL       = `${API_BASE_URL}/api/admin/menus`;
const ROLES_URL       = `${API_BASE_URL}/api/role`;

/** Permission keys — matches backend access schema */
export const PERMS = ["view", "add", "update", "delete"];

function extractError(err) {
  const data = err?.response?.data;
  if (data?.message) return new Error(data.message);
  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    return new Error(data.errors.map((e) => e.msg).filter(Boolean).join(", "));
  }
  return new Error(err?.message || "An unexpected error occurred.");
}

/* ── Roles ────────────────────────────────────────────────────── */

/** Fetch all roles for the role dropdown */
export async function fetchPermissionRoles() {
  try {
    const { data } = await axios.get(ROLES_URL, { headers: authHeaders() });
    const payload = data?.results ?? data?.data ?? data;
    return Array.isArray(payload) ? payload : (payload?.roles ?? []);
  } catch (err) {
    throw extractError(err);
  }
}

/* ── Modules ──────────────────────────────────────────────────── */

function flattenMenus(items = [], acc = [], depth = 0) {
  items.forEach((item) => {
    acc.push({ ...item, depth });
    if (Array.isArray(item.children) && item.children.length) {
      flattenMenus(item.children, acc, depth + 1);
    }
  });
  return acc;
}

/** Fetch all active menus from DB */
export async function fetchModulesList() {
  try {
    const { data } = await axios.get(MENUS_URL, { headers: authHeaders() });
    const tree = data?.results?.data?.menus ?? [];
    const list = flattenMenus(tree);
    return list.filter((m) => m.isActive !== false && !m.isDeleted);
  } catch (err) {
    throw extractError(err);
  }
}

/* ── Grid helpers ─────────────────────────────────────────────── */

/**
 * Build a blank permission grid from a live module list.
 * @param {Array} modules — from fetchModulesList()
 */
export const buildDefaultGrid = (modules = []) =>
  modules.map((m) => ({
    moduleId: m._id,
    module:   m.key,
    view:     false,
    add:      false,
    update:   false,
    delete:   false,
  }));

/** Merge a saved permissions array into a default grid */
function mergePermissions(grid, savedPermissions = []) {
  const permMap = new Map(
    savedPermissions.map((p) => [String(p.menuId?._id || p.menuId), p.permissions || {}])
  );
  return grid.map((row) => {
    const access = permMap.get(String(row.moduleId));
    if (!access) return row;
    return {
      ...row,
      view:   Boolean(access.view),
      add:    Boolean(access.create),
      update: Boolean(access.update),
      delete: Boolean(access.delete),
    };
  });
}

/* ── Fetch role permissions ───────────────────────────────────── */

/**
 * Fetch permissions for a role merged against all DB modules.
 * Returns a grid: [{ moduleId, module, view, add, update, delete }]
 */
export async function fetchRolePermissions(roleId, modules = null) {
  try {
    const [mods, { data }] = await Promise.all([
      modules ? Promise.resolve(modules) : fetchModulesList(),
      axios.get(`${MENUS_URL}/permissions/${roleId}`, { headers: authHeaders() }),
    ]);

    const savedPermissions = data?.results?.data ?? [];
    const grid = buildDefaultGrid(mods);
    return mergePermissions(grid, savedPermissions);
  } catch (err) {
    throw extractError(err);
  }
}

/* ── Save role permissions ────────────────────────────────────── */

/**
 * Save permissions for a role.
 * @param {string} roleId
 * @param {Array}  grid — [{ moduleId, view, add, update, delete }]
 */
export async function saveRolePermissions(roleId, grid) {
  try {
    const permissions = grid.map((row) => ({
      menuId: row.moduleId,
      permissions: {
        view:   Boolean(row.view),
        create: Boolean(row.add),
        update: Boolean(row.update),
        delete: Boolean(row.delete),
      },
    }));

    const { data } = await axios.post(
      `${MENUS_URL}/permissions/${roleId}`,
      { permissions },
      { headers: { ...authHeaders(), "Content-Type": "application/json" } }
    );
    return data;
  } catch (err) {
    throw extractError(err);
  }
}

/* ── Reset role permissions ───────────────────────────────────── */

/**
 * Reset all permissions for a role to false.
 */
export async function resetRolePermissions(roleId, modules = null) {
  try {
    const mods = modules ?? await fetchModulesList();
    const permissions = mods.map((m) => ({
      menuId: m._id,
      permissions: { view: false, create: false, update: false, delete: false },
    }));

    const { data } = await axios.post(
      `${MENUS_URL}/permissions/${roleId}`,
      { permissions },
      { headers: { ...authHeaders(), "Content-Type": "application/json" } }
    );
    return data;
  } catch (err) {
    throw extractError(err);
  }
}

/* ── Fetch all role permissions (for manage page) ─────────────── */

/**
 * Fetch all roles with their permission grids.
 * Returns: [{ role, grid, isAssigned }]
 */
export async function fetchAllRolePermissions() {
  try {
    const [roles, modules] = await Promise.all([
      fetchPermissionRoles(),
      fetchModulesList(),
    ]);

    const results = await Promise.all(
      roles.map(async (role) => {
        try {
          const { data } = await axios.get(
            `${MENUS_URL}/permissions/${role._id}`,
            { headers: authHeaders() }
          );
          const savedPermissions = data?.results?.data ?? [];
          const grid = buildDefaultGrid(modules);
          return {
            role,
            grid: mergePermissions(grid, savedPermissions),
            isAssigned: savedPermissions.length > 0,
          };
        } catch {
          return { role, grid: buildDefaultGrid(modules), isAssigned: false };
        }
      })
    );

    return results;
  } catch (err) {
    throw extractError(err);
  }
}